/**
 * @fileoverview UnifiedCards Component - Flexible card grid with modal integration
 * 
 * Provides a flexible card grid system with:
 * - Configurable card layouts
 * - Optional image display
 * - PDF modal integration
 * - Multi-modal management
 * - Z-index stacking for overlapping modals
 * - Dynamic positioning
 * - Click-to-open functionality
 * - Responsive grid design
 * 
 * @component
 * @example
 * <UnifiedCards 
 *   data={cardData} 
 *   showImages={true} 
 * />
 * 
 * @requires React
 * @requires ./UnifiedCards.css
 * @requires ../../modals/PDFModal
 * 
 * @author By John Bellew - Infinia.ie - last updated 10/07/2025
 */

import React, { useState } from 'react';
import './UnifiedCards.css';
import PDFModal from '../../modals/PDFModal';

/**
 * UnifiedCards Component
 * 
 * Renders a flexible grid of cards with optional images and PDF modal integration.
 * 
 * Features:
 * - Configurable card layouts
 * - Optional image display
 * - PDF modal integration
 * - Multi-modal management
 * - Dynamic positioning
 * - Click-to-open functionality
 * - Responsive grid design
 * 
 * @component
 * @param {Object} props - Component properties
 * @param {Array} props.data - Array of card data objects
 * @param {boolean} [props.showImages=false] - Whether to display card images
 * 
 * @returns {JSX.Element} Unified card grid with modal integration
 */
const UnifiedCards = ({ data, showImages = false, }) => {

    const [modals, setModals] = useState([]);

    // Function to open a PDF modal
    const openPDFModal = (id, title, pdfUrl) => {
        setModals((prevModals) => {
            const existingModal = prevModals.find((modal) => modal.id === id);
            if (existingModal) {
                return prevModals.map((modal) =>
                    modal.id === id ? { ...modal, isOpen: true } : modal
                );
            } else {
                const maxZIndex = Math.max(...prevModals.map((modal) => modal.zIndex), 0) + 1;
                const newModal = {
                    id,
                    title,
                    pdfUrl,
                    isOpen: true,
                    top: `${15 + (prevModals.length * 2)}%`,
                    left: `${30 + (prevModals.length * 2)}%`,
                    zIndex: maxZIndex,
                };
                return [...prevModals, newModal];
            }
        });
    };

    // Close PDF modal by id
    const closePDFModal = (id) => {
        setModals((prevModals) =>
            prevModals.map((modal) =>
                modal.id === id ? { ...modal, isOpen: false } : modal
            )
        );
    };

    return (
        <>
            <section className="infocardgrid-section">
                <div className="infocardgrid-container">
                    <div className="infocardgrid-grid">
                        {data.map((item) => (
                            <div
                                key={item.id}
                                className={`infocardgrid-card ${item.doc ? 'pointer' : ''}`}
                                onClick={item.doc ? () => openPDFModal(item.id, item.title, item.doc) : null}
                            >
                                {showImages && item.image && (
                                    <img src={item.image} alt={item.title} className="infocardgrid-image" />
                                )}
                                <div className="item-header">
                                    <h2 className="sub-header">{item.title}</h2>
                                </div>
                                <div className="item-content">
                                    <p className="summary">{item.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {modals.map((modal) => (
                modal.isOpen && (
                    <PDFModal
                        key={modal.id}
                        listItemId={modal.id}
                        title={modal.title}
                        url={`${modal.pdfUrl}`}
                        show={modal.isOpen}
                        onClose={(e) => {
                            e.stopPropagation();
                            closePDFModal(modal.id);
                        }}
                        allowFullscreen={true}
                        allowMove={true}
                        showLinkinToolBar={true}
                        style={{ top: modal.top, left: modal.left, zIndex: modal.zIndex }}
                    />
                )
            ))}
        </>
    );
};

export default UnifiedCards;
